import * as React from "react"
import Select from "react-select"
import { cn } from "../../lib/utils"
import { Badge } from "./badge"

/**
 * SearchableSelect - A searchable dropdown component with excellent touch support
 * Now powered by react-select for native mobile scrolling
 * 
 * API remains the same for backward compatibility:
 * - options: Array of {value, label, searchText, metadata}
 * - value: selected value
 * - onValueChange: callback(newValue)
 * - placeholder, emptyMessage, disabled, allowClear, showMetadata
 */
export function SearchableSelect({
  options = [],
  value,
  onValueChange,
  placeholder = "Select...",
  emptyMessage = "No results found.",
  className,
  disabled = false,
  allowClear = true,
  showMetadata = true,
  ...props
}) {
  // Convert options to react-select format
  const selectOptions = React.useMemo(() => {
    return options.map(opt => ({
      value: opt.value,
      label: opt.label,
      searchText: opt.searchText || '',
      metadata: opt.metadata || null
    }))
  }, [options])

  // Find selected option - also handle case where value exists but isn't in options
  const selectedValue = React.useMemo(() => {
    if (!value) return null
    const found = selectOptions.find(opt => opt.value === value)
    if (found) return found
    // If value exists but not in options, create a temporary option for display
    return { value: value, label: value, searchText: '', metadata: null }
  }, [value, selectOptions])

  // Custom filter function to search across label, searchText, and metadata
  const filterOption = (option, inputValue) => {
    if (!inputValue) return true
    
    const query = inputValue.toLowerCase()
    const { label, searchText, metadata } = option.data
    
    if (label?.toLowerCase().includes(query)) return true
    if (searchText?.toLowerCase().includes(query)) return true
    
    if (metadata) {
      const metadataMatch = Object.values(metadata).some(val =>
        val?.toString().toLowerCase().includes(query)
      )
      if (metadataMatch) return true
    }
    
    return false
  }

  // Custom option renderer with metadata badges
  const formatOptionLabel = (option, { context }) => {
    if (context === 'value') {
      return <span className="truncate">{option.label}</span>
    }
    
    return (
      <div className="flex flex-col gap-1 py-0.5">
        <span className="font-medium text-sm">{option.label}</span>
        {showMetadata && option.metadata && (
          <div className="flex flex-wrap gap-1">
            {Object.entries(option.metadata).map(([key, val]) => (
              val && (
                <span
                  key={key}
                  className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600"
                >
                  {key}: {val}
                </span>
              )
            ))}
          </div>
        )}
        {option.searchText && !option.metadata && (
          <span className="text-xs text-slate-500">
            {option.searchText}
          </span>
        )}
      </div>
    )
  }

  // Custom styles matching shadcn/ui design
  const customStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '40px',
      borderColor: state.isFocused ? '#a3e635' : '#e2e8f0',
      borderRadius: '0.375rem',
      backgroundColor: 'white',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(163, 230, 53, 0.3)' : 'none',
      '&:hover': {
        borderColor: '#a3e635'
      },
      cursor: 'pointer'
    }),
    menu: (base) => ({
      ...base,
      zIndex: 99999,
      borderRadius: '0.375rem',
      border: '1px solid #e2e8f0',
      backgroundColor: 'white',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      marginTop: '4px',
      position: 'absolute'
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 99999
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: '300px',
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      padding: '4px'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused 
        ? '#f1f5f9' 
        : state.isSelected 
          ? '#e2e8f0' 
          : 'transparent',
      color: '#1e293b',
      cursor: 'pointer',
      borderRadius: '0.25rem',
      padding: '8px 12px',
      '&:active': {
        backgroundColor: '#e2e8f0'
      }
    }),
    input: (base) => ({
      ...base,
      color: '#1e293b',
      margin: 0,
      padding: 0
    }),
    placeholder: (base) => ({
      ...base,
      color: '#94a3b8'
    }),
    singleValue: (base) => ({
      ...base,
      color: '#1e293b'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: '#94a3b8',
      padding: '8px',
      transition: 'transform 0.2s',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0)',
      '&:hover': {
        color: '#64748b'
      }
    }),
    clearIndicator: (base) => ({
      ...base,
      color: '#94a3b8',
      padding: '8px',
      '&:hover': {
        color: '#ef4444'
      }
    }),
    noOptionsMessage: (base) => ({
      ...base,
      color: '#94a3b8',
      padding: '24px 16px',
      textAlign: 'center'
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '2px 8px'
    })
  }

  return (
    <Select
      options={selectOptions}
      value={selectedValue}
      onChange={(selected) => {
        console.log('SearchableSelect onChange:', selected);
        onValueChange?.(selected?.value || '')
      }}
      placeholder={placeholder}
      isDisabled={disabled}
      isClearable={allowClear}
      isSearchable={true}
      filterOption={filterOption}
      formatOptionLabel={formatOptionLabel}
      noOptionsMessage={() => emptyMessage}
      styles={customStyles}
      className={cn("searchable-select", className)}
      classNamePrefix="ss"
      menuPlacement="auto"
      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
      menuShouldScrollIntoView={false}
      blurInputOnSelect={true}
      closeMenuOnSelect={true}
      {...props}
    />
  )
}
